import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, ArrowRight, Loader2, Send } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import OnboardingLayout from "../components/onboarding/OnboardingLayout";
import CompanyWalletStep from "../components/onboarding/steps/CompanyInfoStep";
import ContactDetailsStep from "../components/onboarding/steps/ContactDetailsStep";
import ReviewStep from "../components/onboarding/steps/ReviewStep";
import { Button } from "../components/ui/button";
import { fullApplicationSchema, stepSchemas } from "../schemas/onboarding";
import {
	createApplication,
	getApplication,
	saveApplicationDraft,
	submitApplication,
} from "../services/api/applicationsApi";
import type { OnboardingFormData } from "../types/onboarding";
import { WIZARD_STEPS } from "../types/onboarding";
import { notifications } from "../utils/notifications";

const STORAGE_KEY = "onboarding_session";
const AUTOSAVE_INTERVAL = 30_000; // 30 seconds

const Onboard: React.FC = () => {
	const [currentStep, setCurrentStep] = useState(1);
	const [applicationId, setApplicationId] = useState<string | null>(null);
	const [applicationToken, setApplicationToken] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const lastSavedRef = useRef<string>("");

	const methods = useForm<OnboardingFormData>({
		resolver: zodResolver(fullApplicationSchema) as any,
		mode: "onTouched",
		defaultValues: {
			company_name: "",
			wallet_name: "",
			custodial_type: "Custodial",
			chain: "",
			asset: "",
			products: [],
			country_of_incorporation: "",
			website: "",
			business_description: "",
			primary_contact: { name: "", email: "", phone: "", role: "" },
			terms_accepted: false,
		},
	});

	// Initialize — create new or resume existing application
	useEffect(() => {
		const init = async () => {
			try {
				const stored = localStorage.getItem(STORAGE_KEY);
				if (stored) {
					const { id, token } = JSON.parse(stored);
					const app = await getApplication(id, token);
					if (app.status === "draft") {
						setApplicationId(id);
						setApplicationToken(token);
						setCurrentStep(app.current_step || 1);
						const formData = app as unknown as Partial<OnboardingFormData>;
						for (const [key, value] of Object.entries(formData)) {
							if (value !== undefined && key in methods.getValues()) {
								methods.setValue(
									key as keyof OnboardingFormData,
									value as never,
								);
							}
						}
						setIsLoading(false);
						return;
					}
					localStorage.removeItem(STORAGE_KEY);
				}

				const { id, application_token } = await createApplication();
				setApplicationId(id);
				setApplicationToken(application_token);
				localStorage.setItem(
					STORAGE_KEY,
					JSON.stringify({ id, token: application_token }),
				);
			} catch (err) {
				console.error("Failed to initialize onboarding:", err);
				localStorage.removeItem(STORAGE_KEY);
				notifications.error("Failed to start application. Please try again.");
			} finally {
				setIsLoading(false);
			}
		};
		init();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Autosave draft
	const saveDraft = useCallback(async () => {
		if (!applicationId || !applicationToken) return;
		const data = methods.getValues();
		const serialized = JSON.stringify(data);
		if (serialized === lastSavedRef.current) return;

		try {
			await saveApplicationDraft(applicationId, applicationToken, {
				...data,
				current_step: currentStep,
			} as unknown as Partial<OnboardingFormData> & { current_step: number });
			lastSavedRef.current = serialized;
		} catch (err) {
			console.error("Autosave failed:", err);
		}
	}, [applicationId, applicationToken, currentStep, methods]);

	useEffect(() => {
		const timer = setInterval(saveDraft, AUTOSAVE_INTERVAL);
		return () => clearInterval(timer);
	}, [saveDraft]);

	// Step navigation
	const goNext = async () => {
		const stepSchema = stepSchemas[currentStep as keyof typeof stepSchemas];
		if (stepSchema) {
			const data = methods.getValues();
			const result = stepSchema.safeParse(data);
			if (!result.success) {
				await methods.trigger();
				// Scroll to first error field
				setTimeout(() => {
					const firstError = document.querySelector('[role="alert"]');
					if (firstError) {
						firstError.scrollIntoView({ behavior: "smooth", block: "center" });
					}
				}, 100);
				notifications.error(
					"Please fill in all required fields before continuing.",
				);
				return;
			}
		}
		await saveDraft();
		setCurrentStep((s) => Math.min(s + 1, WIZARD_STEPS.length));
	};

	const goBack = () => {
		setCurrentStep((s) => Math.max(s - 1, 1));
	};

	// Final submit
	const handleSubmit = async () => {
		if (!applicationId || !applicationToken) return;

		const isValid = await methods.trigger();
		if (!isValid) {
			setTimeout(() => {
				const firstError = document.querySelector('[role="alert"]');
				if (firstError) {
					firstError.scrollIntoView({ behavior: "smooth", block: "center" });
				}
			}, 100);
			notifications.error(
				"Please complete all required fields before submitting.",
			);
			return;
		}

		setIsSubmitting(true);
		try {
			await saveDraft();
			await submitApplication(applicationId, applicationToken);
			setIsSubmitted(true);
			localStorage.removeItem(STORAGE_KEY);
			notifications.success("Application submitted successfully!");
		} catch (err) {
			console.error("Submit failed:", err);
			notifications.error("Failed to submit application. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	// Success screen
	if (isSubmitted) {
		return (
			<OnboardingLayout currentStep={WIZARD_STEPS.length}>
				<div
					style={{
						textAlign: "center",
						padding: "3rem 1rem",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: "1rem",
					}}
				>
					<div
						style={{
							width: "4rem",
							height: "4rem",
							borderRadius: "50%",
							background: "var(--color-success)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Send size={24} color="#fff" />
					</div>
					<h2
						style={{
							fontSize: "1.5rem",
							fontWeight: 600,
							color: "var(--color-text-primary)",
						}}
					>
						Application Submitted
					</h2>
					<p
						style={{
							fontSize: "0.95rem",
							color: "var(--color-text-secondary)",
							maxWidth: "28rem",
						}}
					>
						Thank you for your application. Our team will review it and get back
						to you at the email address you provided. You will receive a
						confirmation email shortly.
					</p>
				</div>
			</OnboardingLayout>
		);
	}

	// Loading screen
	if (isLoading) {
		return (
			<OnboardingLayout currentStep={1}>
				<div
					style={{ display: "flex", justifyContent: "center", padding: "3rem" }}
				>
					<Loader2
						size={32}
						style={{
							animation: "spin 1s linear infinite",
							color: "var(--color-primary)",
						}}
					/>
				</div>
			</OnboardingLayout>
		);
	}

	// Render current step
	const renderStep = () => {
		switch (currentStep) {
			case 1:
				return <CompanyWalletStep />;
			case 2:
				return <ContactDetailsStep />;
			case 3:
				return <ReviewStep />;
			default:
				return null;
		}
	};

	return (
		<OnboardingLayout currentStep={currentStep}>
			<FormProvider {...methods}>
				<form onSubmit={(e) => e.preventDefault()}>
					{renderStep()}

					{/* Navigation Buttons */}
					<div
						style={{
							display: "flex",
							justifyContent: currentStep > 1 ? "space-between" : "flex-end",
							marginTop: "2rem",
							paddingTop: "1.5rem",
							borderTop: "1px solid var(--color-border)",
						}}
					>
						{currentStep > 1 && (
							<Button type="button" variant="outline" onClick={goBack}>
								<ArrowLeft size={16} style={{ marginRight: "0.35rem" }} />
								Back
							</Button>
						)}

						{currentStep < WIZARD_STEPS.length ? (
							<Button type="button" onClick={goNext}>
								Next
								<ArrowRight size={16} style={{ marginLeft: "0.35rem" }} />
							</Button>
						) : (
							<Button
								type="button"
								onClick={handleSubmit}
								disabled={isSubmitting}
							>
								{isSubmitting ? (
									<>
										<Loader2
											size={16}
											style={{
												marginRight: "0.35rem",
												animation: "spin 1s linear infinite",
											}}
										/>
										Submitting...
									</>
								) : (
									<>
										<Send size={16} style={{ marginRight: "0.35rem" }} />
										Submit Application
									</>
								)}
							</Button>
						)}
					</div>
				</form>
			</FormProvider>
		</OnboardingLayout>
	);
};

export default Onboard;
